/* 151C §6 — THE STAGE LINK IS THE PURPLE LINK.
 *
 * RULED: choosing "Link to Stage" while adding a document to a task/event
 * creates the STANDARD purple task/event↔stage link — the itemLinks row the
 * links system (111 §5) draws in `--hll` and unlinks in the usual places —
 * and the stageId mechanism CC wired the doc dialog to in 151 §2 step 4 is
 * retired FROM THAT FLOW (its visible tell was the amber .evHl jump of the
 * Show-Stage pill; measured: no other amber exists for it). The parent's own
 * StageLinkField keeps editing `stageId` — the belongs-to fact is not this
 * file's business.
 *
 * The write is reconciliation at doc-save: create the pair if missing (the
 * server dedupes — `existing: true` comes back rather than a duplicate), and
 * delete whatever DIRECT stage links a changed or cleared choice leaves
 * behind. DIRECT, never the closure walk: this dialog edits one edge, not
 * the cluster. No local patcher is owed — the server fires link-create/
 * link-delete pokes and App refetches the matter row (App.tsx `case`/`cc`
 * SSE arms), sender included.
 */
import { createItemLink, deleteItemLink } from "../api";
import { linksOfItem, type ItemLinkRow, type LinkRef } from "./linkHighlight";

const otherOf = (l: ItemLinkRow, self: LinkRef): LinkRef =>
  l.a.k === self.k && Number(l.a.id) === Number(self.id) ? l.b : l.a;

/** the item's DIRECT stage partners, with the link rows that carry them */
export const directStageLinks = (
  matterRow: { itemLinks?: unknown } | null | undefined,
  item: LinkRef,
): { linkId: number; stageId: number }[] =>
  linksOfItem(matterRow, item)
    .map((l) => ({ linkId: Number(l.id), other: otherOf(l, item) }))
    .filter((x) => x.other.k === "stage")
    .map((x) => ({ linkId: x.linkId, stageId: Number(x.other.id) }));

/** what the dialog's select shows: the first direct stage partner, if any */
export const directLinkedStageId = (
  matterRow: { itemLinks?: unknown } | null | undefined,
  item: LinkRef,
): number | null => {
  const d = directStageLinks(matterRow, item);
  return d.length ? d[0].stageId : null;
};

export async function reconcileStageLink(opts: {
  matterKind: "ca" | "cc";
  matterId: number;
  matterRow: { itemLinks?: unknown } | null | undefined;
  item: LinkRef;
  stageId: number | null;
}): Promise<void> {
  const { matterKind, matterId, matterRow, item, stageId } = opts;
  const cur = directStageLinks(matterRow, item);
  for (const c of cur) {
    if (stageId != null && c.stageId === stageId) continue;
    await deleteItemLink(matterKind, matterId, c.linkId);
  }
  if (stageId != null && !cur.some((c) => c.stageId === stageId)) {
    await createItemLink(matterKind, matterId, item, { k: "stage", id: stageId });
  }
}
